import axios from 'axios'
import React, {useCallback, useEffect, useState} from 'react'
import {useParams} from 'react-router-dom'
import {toast} from 'react-toastify'
import {API_ENDPOINTS} from '../../../../constants/apiConstants'
import {ReadingModel, ApiResponseModel} from '../../../../entities/entities'
import HealthCriticalityAlertsTable from '../notifications/HealthCriticalityAlertsTable'
import MetaTags from '../../../../meta/MetaTags'
import {useReduxAuth} from '../../../auth'
import {USER_ROLES} from '../../../../constants/StatusConstants'
import {useMADoctorDropdown} from '../../context/MADoctorDropdownContext'

const PatientHealthCriticalityAlerts = () => {
  const {patientId} = useParams()
  const {currentUser} = useReduxAuth()
  const roleId = currentUser?.role_id
  
  // MA Doctor Dropdown
  const maDropdown = useMADoctorDropdown()
  const maSelectedDoctorId = roleId === USER_ROLES.MEDICAL_ASSISTANT ? maDropdown.selectedDoctorId : undefined
  
  const [healthAlerts, setHealthAlerts] = useState<ReadingModel[]>([])
  const [sortBy, setSortBy] = useState<string>('severity')
  const [loadingRowId, setLoadingRowId] = useState<number | null>(null)

  // Handler to update sort option
  const handleSortByFilterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedValue = e.target.value
    setSortBy(selectedValue)
    fetchHealthCriticalityAlerts(selectedValue)
  }
  
  const fetchHealthCriticalityAlerts = useCallback(async (sortBy?: string | null) => {
    try {
      // Build query params - include doctor_id for MA
      const doctorIdParam = roleId === USER_ROLES.MEDICAL_ASSISTANT && maSelectedDoctorId
        ? `doctor_id=${maSelectedDoctorId}`
        : null
      const response = await axios.get<ApiResponseModel<{alerts: ReadingModel[]}>>(
        API_ENDPOINTS.GET_HEALTH_ALERTS(patientId, doctorIdParam)
      )
      const allHealthAlerts = response.data?.data?.alerts || []
      setHealthAlerts(allHealthAlerts)
    } catch (error) {
      if (axios.isAxiosError(error)) {
        toast.error(error.response?.data?.message || 'Something went wrong')
      } else if (error instanceof Error) {
        toast.error(error.message)
      } else {
        toast.error('An unexpected error occurred')
      }
    }
  }, [patientId, roleId, maSelectedDoctorId])

  useEffect(() => {
    fetchHealthCriticalityAlerts()
  }, [fetchHealthCriticalityAlerts])

  const handleChangeReviewedStatus = async (id?: string | number) => {
    if (!id) return
    
    setLoadingRowId(Number(id))
    try{
      await axios.get(
        API_ENDPOINTS.GET_HEALTH_ALERT_REVIEWED(id)
      )
      // fetch updated alerts
      fetchHealthCriticalityAlerts()
    }
    catch (error) {
      if (axios.isAxiosError(error)) {
        toast.error(error.response?.data?.message || 'Something went wrong')
      } else if (error instanceof Error) {
        toast.error(error.message)
      } else {
        toast.error('An unexpected error occurred')
      }
    } finally {
      setLoadingRowId(null)
    }
  }
  return (
    <>
      <MetaTags title={'Health Criticality Alerts'} />
      <div className='card mb-5 mb-xl-10' id='kt_profile_details_view'>
        <div className='card-header d-flex justify-content-between align-items-center'>
          <h3 className='fw-bolder m-0'>All Health Alerts</h3>
        </div>

        <div>
          <HealthCriticalityAlertsTable
            healthAlerts={healthAlerts}
            title={'Health Criticality Alerts'}
            onReload={() => fetchHealthCriticalityAlerts(sortBy)}
            hidePatientName={true}
            showHeader={false}
            sortByFilter={sortBy}
            handleSortByFilter={handleSortByFilterChange}
            handleReviewToggle={handleChangeReviewedStatus}
            loadingRowId={loadingRowId}
          />
        </div>
      </div>
    </>
  )
}

export default PatientHealthCriticalityAlerts
